import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CompanyExpenseCategory } from '../../types/company-expense';
import { CompanyExpensePaymentsReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCompanyExpensePayments } from './exportCompanyExpenseReports';

export function CompanyExpensePaymentsPage() {
  const labels = t().reports;
  const [categories, setCategories] = useState<CompanyExpenseCategory[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [report, setReport] = useState<CompanyExpensePaymentsReport | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getCompanyExpenseCategories(false).then(setCategories);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpensePaymentsReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, categoryId]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          report ? (
            <ReportExportButtons
              disabled={!report}
              onExcel={() =>
                exportCompanyExpensePayments({ report, format: 'excel' })
              }
              onPdf={() =>
                exportCompanyExpensePayments({ report, format: 'pdf' })
              }
            />
          ) : null
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().companyExpenses.category}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.allCategories}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(e) => setCategoryId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : !report || report.lines.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.companyExpensePaymentDate}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.companyExpenseVendor}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.companyExpenseDocumentNo}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t().companyExpenses.category}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseNet}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseVat}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseGross}
                </th>
              </tr>
            </thead>
            <tbody>
              {report.lines.map((line) => (
                <tr key={line.paymentId} className="border-t border-line">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Link
                      to={`/company-expenses/${line.expenseId}/edit`}
                      className="text-brand hover:underline"
                    >
                      {formatDate(line.paymentDate)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{line.vendorName || '—'}</td>
                  <td className="px-3 py-2">{line.documentNo || '—'}</td>
                  <td className="px-3 py-2">{line.categoryName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(line.netAmount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(line.vatAmount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(line.grossAmount)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-line bg-surface-muted/40 font-semibold">
                <td className="px-3 py-2" colSpan={4}>
                  {t().common.total} ({report.totals.count})
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.net)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.vat)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.gross)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
